import { memo, useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import { GridRowModel, GridRowSelectionModel } from "@mui/x-data-grid-pro";
import {
  Field,
  FieldHelperProps,
  FieldInputProps,
  FieldMetaProps,
  useField,
  useFormikContext,
} from "formik";
import { useIntl } from "react-intl";

import { Row } from "../Layout/utils";
import CustomDatePicker from "../components/Inputs/DatePickers/CustomDatePicker";
import {
  CalculEditorFormDetails,
  CalculEditorSimulationFormDetails,
  ChartFormDetails,
  ConfigurationFormType,
  ConfigurationPageTable,
  DateRangePickerFormDetails,
  FieldGroupColumnFormDetailsFilled,
  MultiApiTypeFormDetails,
  RadioGroupFormDetails,
  RemoteLookupFormDetails,
  SelectFilterItemSortedFormDetails,
  StaticSelectFormDetails,
  TextFormDetails,
  WarningFormDetails,
  filterType,
} from "./interfaces";
import { SelectDayOfWeek } from "./modalInputs/SelectDayOfWeek";
import { SelectFilterItemSorted } from "./modalInputs/SelectFilterItemSorted";
import { SelectNbStars } from "./modalInputs/SelectNbStars";
import { StaticSelector } from "./modalInputs/StaticSelector";
import { MultiSelect } from "./modalInputs/MultiSelect";
import FormikDateRangePicker from "../components/Inputs/DatePickers/FormikDateRangePicker";
import { TooltipLabel } from "../components/Tooltips";
import { GenericObject } from "../services/Price";
import { SelectMultipleValues } from "./modalInputs/SelectMultipleValues";
import { WeekDaysRounded } from "./modalInputs/WeekDaysRounded";
import AutoFilledInput from "./modalInputs/AutoFilledInput";
import { BusinessRuleParameter } from "./modalInputs/BusinessRuleParameter/BusinessRuleParameter";
import { BusinessRuleCondition } from "./modalInputs/BusinessRuleCondition/BusinessRuleCondition";
import { BusinessRuleAction } from "./modalInputs/BusinessRuleAction";
import InputTextField from "./modalInputs/InputTextField";
import { formInputsStyles } from "./styles";
import FieldGroup from "./modalInputs/FieldGroup";
import PreviewBlock from "./modalInputs/PreviewBlock";
import ErrorMessage from "./modalInputs/ErrorMessage";
import { TogggleSection } from "./modalInputs/ToggleSection";
import SectionSeparator from "../components/SectionSeparator";
import CheckBoxInput from "./modalInputs/CheckBoxInput";
import CalculEditorInput from "./modalInputs/CalculEditorInput";
import CalculEditorSimulationInput from "./modalInputs/CalculEditorSimulation";
import TextMessage from "./modalInputs/TextMessage";
import FieldGroupColumn from "./modalInputs/FieldGroupColumn";
import { useMaxFromApi } from "./utils";
import TextToggledField from "./modalInputs/TextToggledField";
import RadioGroupButtons from "./modalInputs/RadioGroupButtons";
import RemoteLookup from "./modalInputs/RemoteLookup";
import ChartField from "./modalInputs/ChartField";

// Hide this field when the related field IS filled, or holds `equals` when
// set. Mirror of "show" but inverted, and intentionally not gated on field
// type — works for any pair of fields (e.g. mutually-exclusive FK selectors
// like uh_type / uh_cat on PriceBoundsRule). Declare the action on BOTH
// fields, each pointing at the other. Shared with the toggle-group branch of
// FormBuilder so a hidden toggle takes its children along.
const isHiddenByRelatedInput = (
  filter: filterType,
  values: GenericObject
): boolean => {
  const action = filter.relatedInputAction;
  if (action?.action !== "hide" || typeof action.relatedInput !== "string") {
    return false;
  }
  const related = values[action.relatedInput];
  return action.equals === undefined ? !!related : related === action.equals;
};

const setVisibleState = (
  filter: any,
  toggleMap: Record<string, boolean>
): boolean => {
  return filter.toggleParent
    ? toggleMap[filter.toggleParent] ?? false
    : filter?.visible ?? true;
};

type InputBuilderProps = {
  dynamicPath?: boolean;
  filter: filterType;
  isMappingUhCat?: boolean;
  keyFieldPath?: string;
  multipleSelection?: GridRowSelectionModel;
  isNestedForm?: boolean;
  onDisplaySubCreationForm?: (
    formOptions: ConfigurationPageTable,
    parentFormValues: GenericObject,
    initialValues: GenericObject
  ) => void;
  row?: GridRowModel;
  rows?: GridRowModel[];
  setToggleState: (key: string, value: boolean) => void;
  toggleMap: Record<string, boolean>;
  contrastedStyles?: boolean;
};

type FieldRendererProps = {
  field: FieldInputProps<any>;
  meta: FieldMetaProps<any>;
  helpers: FieldHelperProps<any>;
  filter: filterType;
  row?: GridRowModel;
  rows?: GridRowModel[];
  multipleSelection?: GridRowSelectionModel;
  isMappingUhCat?: boolean;
  dynamicPath?: boolean;
  onDisplaySubCreationForm?: (
    formOptions: ConfigurationPageTable,
    parentFormValues: GenericObject,
    initialValues: GenericObject
  ) => void;
  toggleMap: Record<string, boolean>;
  setToggleState: (key: string, value: boolean) => void;
};

/**
 * Generate input according it type
 */
const FieldRenderer = ({
  field,
  meta,
  helpers,
  filter,
  row,
  rows,
  multipleSelection,
  isMappingUhCat,
  dynamicPath,
  onDisplaySubCreationForm,
  toggleMap,
  setToggleState,
}: FieldRendererProps) => {
  const intl = useIntl();
  const { classes } = formInputsStyles();

  // TODO: Rework this because performance and logic problem
  //
  const [visible, setVisible] = useState<boolean>(
    setVisibleState(filter, toggleMap)
  );

  useEffect(() => {
    setVisible(setVisibleState(filter, toggleMap));
  }, [visible, filter, toggleMap]);
  ////////////////////////////////////

  if (
    filter.type === ConfigurationFormType.Input ||
    filter.type === ConfigurationFormType.Preview ||
    filter.type === ConfigurationFormType.TextArea
  ) {
    return (
      <InputTextField
        classes={classes}
        field={field}
        filter={filter}
        helpers={helpers}
        meta={meta}
      />
    );
  } else if (filter.type === ConfigurationFormType.TextToggled) {
    return (
      <TextToggledField
        classes={classes}
        field={field}
        filter={filter}
        helpers={helpers}
        meta={meta}
      />
    );
  } else if (filter.type === ConfigurationFormType.Check && visible) {
    return (
      <CheckBoxInput
        classes={classes}
        field={field}
        filter={filter}
        helpers={helpers}
        meta={meta}
        row={row}
      />
    );
  } else if (filter.type === ConfigurationFormType.WeekDaysRounded) {
    return (
      <WeekDaysRounded
        filter={filter}
        name={field.name}
        onChange={(weekdays) => {
          helpers.setValue(weekdays);
        }}
        value={field.value}
      />
    );
  } else if (filter.type === ConfigurationFormType.BusinessRuleAction) {
    return (
      <BusinessRuleAction
        filter={filter}
        name={field.name}
        helpers={helpers}
        classes={classes}
        value={field.value}
      />
    );
  } else if (filter.type === ConfigurationFormType.BusinessRulePerimeter) {
    return (
      <BusinessRuleParameter
        filter={filter}
        name={field.name}
        helpers={helpers}
        value={field.value}
      />
    );
  } else if (filter.type === ConfigurationFormType.BusinessRuleCondition) {
    return (
      <BusinessRuleCondition
        filter={filter}
        name={field.name}
        helpers={helpers}
        value={field.value}
        row={row}
      />
    );
  } else if (filter.type === ConfigurationFormType.Select) {
    return (
      <SelectFilterItemSorted
        filter={filter as SelectFilterItemSortedFormDetails}
        field={field}
        meta={meta}
        helpers={helpers}
        classes={classes}
        row={row}
        dynamicPath={dynamicPath}
        rows={rows}
        multipleSelection={multipleSelection}
        isMappingUhCat={isMappingUhCat}
        onDisplaySubCreationForm={onDisplaySubCreationForm}
      />
    );
  } else if (filter.type === ConfigurationFormType.SelectValues) {
    return (
      <SelectMultipleValues
        filter={filter}
        field={field}
        meta={meta}
        helpers={helpers}
        classes={classes}
        row={row}
        dynamicPath={dynamicPath}
      />
    );
  } else if (filter.type === ConfigurationFormType.DayOfWeek) {
    // we can either check the value from ev here or in the component
    return (
      <SelectDayOfWeek
        classes={classes}
        filter={filter}
        name={field.name}
        onChange={(ev) => helpers.setValue(ev.target.value)}
        value={field.value}
      />
    );
  } else if (filter.type === ConfigurationFormType.DatePicker) {
    return (
      <CustomDatePicker
        label={
          filter.tooltip ? (
            <TooltipLabel
              label={intl.formatMessage({ id: filter.label })}
              tooltipLabel={intl.formatMessage({ id: filter.tooltip })}
              translateTooltip={false}
            />
          ) : (
            filter.label
          )
        }
        translateLabel={!filter.tooltip}
        disabled={filter.readonly}
        value={field.value}
        views={filter.views ?? undefined}
        error={meta.touched && !!meta.error}
        helperText={meta.touched ? meta.error : undefined}
        onChange={(isoStr) => helpers.setValue(isoStr)}
      />
    );
  } else if (filter.type === ConfigurationFormType.DateRangePicker) {
    const drpFilter = filter as DateRangePickerFormDetails;
    return (
      <FormikDateRangePicker
        startFieldName={`${field.name}.${drpFilter.startDateField}`}
        endFieldName={`${field.name}.${drpFilter.endDateField}`}
        startLabel={drpFilter.startDateLabel}
        endLabel={drpFilter.endDateLabel}
        startTooltip={drpFilter.startDateTooltip}
        endTooltip={drpFilter.endDateTooltip}
        disablePast={drpFilter.disablePast}
        lockPast={drpFilter.lockPast}
        showClearButtons
      />
    );
  } else if (filter.type === ConfigurationFormType.SelectStars) {
    return (
      <SelectNbStars
        filter={filter}
        field={field}
        helpers={helpers}
        classes={classes}
        multipleSelection={
          multipleSelection !== undefined && multipleSelection.ids.size > 0
        }
      />
    );
  } else if (filter.type === ConfigurationFormType.Separator) {
    return (
      <SectionSeparator
        label={filter.label}
        subLabel={filter.subLabel}
        tooltip={filter.tooltip}
      />
    );
  } else if (filter.type === ConfigurationFormType.Text) {
    const filterData = filter as TextFormDetails;
    return <TextMessage field={filterData} classes={classes} row={row} />;
  } else if (filter.type === ConfigurationFormType.StaticSelect && visible) {
    return (
      <StaticSelector
        filter={filter as StaticSelectFormDetails}
        field={field}
        meta={meta}
        helpers={helpers}
      />
    );
  } else if (filter.type === ConfigurationFormType.MultiSelect) {
    return (
      <MultiSelect
        filter={filter as MultiApiTypeFormDetails}
        field={field}
        meta={meta}
        helpers={helpers}
        row={row}
      />
    );
  } else if (filter.type === ConfigurationFormType.AutoFilledInput) {
    return (
      <AutoFilledInput
        filter={filter}
        field={field}
        classes={classes}
        row={row}
        dynamicPath={dynamicPath}
        rows={rows}
      />
    );
  } else if (filter.type === ConfigurationFormType.FieldGroup) {
    return (
      <Field name={field.name}>
        {({ field }: { field: FieldInputProps<any> }) => (
          <FieldGroup
            fieldsOptions={filter}
            field={field}
            row={row}
            rows={rows}
            multipleSelection={multipleSelection}
            dynamicPath={dynamicPath}
          />
        )}
      </Field>
    );
  } else if (filter.type === ConfigurationFormType.PreviewBlock && row) {
    return <PreviewBlock fieldsOptions={filter} field={field} row={row} />;
  } else if (filter.type === ConfigurationFormType.ErrorMessage && row) {
    return <ErrorMessage fieldsOptions={filter} row={row} field={field} />;
  } else if (filter.type === ConfigurationFormType.ToggleSection) {
    return (
      <TogggleSection
        filter={filter}
        row={row}
        classes={classes}
        toggleMap={toggleMap}
        setToggleState={setToggleState}
      />
    );
  } else if (filter.type === ConfigurationFormType.CalculEditor) {
    return (
      <CalculEditorInput
        classes={classes}
        field={field}
        filter={filter as CalculEditorFormDetails}
        meta={meta}
      />
    );
  } else if (filter.type === ConfigurationFormType.CalculEditorSimulation) {
    return (
      <CalculEditorSimulationInput
        classes={classes}
        field={field}
        filter={filter as CalculEditorSimulationFormDetails}
        meta={meta}
      />
    );
  } else if (filter.type === ConfigurationFormType.Warning) {
    const { component: WarningComponent } = filter as WarningFormDetails;
    return <WarningComponent row={row} />;
  } else if (filter.type === ConfigurationFormType.FieldGroupColumn) {
    const filterData = filter as FieldGroupColumnFormDetailsFilled;
    return (
      <Field name={field.name}>
        {({ field }: { field: FieldInputProps<any> }) => (
          <FieldGroupColumn
            meta={meta}
            fieldsOptions={filterData}
            field={field}
            row={row}
            rows={rows}
            multipleSelection={multipleSelection}
            dynamicPath={dynamicPath}
          />
        )}
      </Field>
    );
  } else if (filter.type === ConfigurationFormType.RadioGroup) {
    return (
      <RadioGroupButtons
        filter={filter as RadioGroupFormDetails}
        field={field}
        meta={meta}
        helpers={helpers}
      />
    );
  } else if (filter.type === ConfigurationFormType.RemoteLookup) {
    return (
      <RemoteLookup
        filter={filter as RemoteLookupFormDetails}
        field={field}
        row={row}
      />
    );
  } else if (filter.type === ConfigurationFormType.Chart) {
    return <ChartField filter={filter as ChartFormDetails} row={row} />;
  }

  return <></>;
};

const InputBuilder = (props: InputBuilderProps) => {
  const {
    dynamicPath,
    filter,
    isMappingUhCat,
    keyFieldPath,
    multipleSelection,
    isNestedForm,
    onDisplaySubCreationForm,
    row,
    rows,
    setToggleState,
    toggleMap,
    contrastedStyles,
  } = props;
  const { classes } = formInputsStyles();
  const keyInputPath = keyFieldPath
    ? `${keyFieldPath}.${filter.key.replace(/\./g, "__")}`
    : filter.key.split(".").join("__");

  const intl = useIntl();
  const { values } = useFormikContext<any>();
  const maxValue = useMaxFromApi(values, keyInputPath, filter.maxFromApi);

  const validateMax = useCallback(
    (value: any) => {
      if (
        maxValue === undefined ||
        maxValue === null ||
        !filter.maxFromApi?.errorKey
      )
        return undefined;
      const num = Number(value);
      if (value !== "" && value != null && !isNaN(num) && num > maxValue) {
        return intl.formatMessage(
          { id: filter.maxFromApi.errorKey },
          { max: maxValue }
        );
      }

      return undefined;
    },
    [maxValue, filter.maxFromApi?.errorKey, intl]
  );

  const [field, meta, helpers] = useField(
    filter.maxFromApi?.errorKey
      ? { name: keyInputPath, validate: validateMax ?? true }
      : keyInputPath
  );
  // Honor `toggleParent` at the wrapper level too. Without this, field types
  // that don't internally check `visible` (e.g. Select) keep rendering even
  // when their toggle section is closed, leaving the gated fields visible.
  const visible = setVisibleState(filter, toggleMap);

  // Check if current field must be created
  if (!visible) return null;
  if (filter.type === ConfigurationFormType.Preview && !field.value)
    return null;
  if (filter.type === ConfigurationFormType.PreviewBlock) {
    if (
      !field.value ||
      (field.value &&
        !filter.showWhenFalsy &&
        (field.value?.value === null ||
          field.value?.value === undefined ||
          field.value?.value === false ||
          field.value?.value === 0))
    )
      return null;

    const exists = Object.keys(field.value.datasPreview).some((k) => {
      return field.value.datasPreview[k].value;
    });

    if (!exists) return null;
  }
  if (
    filter.type === ConfigurationFormType.ErrorMessage &&
    (field.value?.value === null ||
      field.value?.value === undefined ||
      field.value?.value === 1 ||
      field.value?.value === true)
  )
    return null;
  if (
    (filter.type === ConfigurationFormType.FieldGroup ||
      filter.type === ConfigurationFormType.FieldGroupColumn) &&
    filter.relatedInputAction?.action === "show" &&
    typeof filter.relatedInputAction.relatedInput === "string" &&
    !values[filter.relatedInputAction.relatedInput]
  ) {
    return null;
  }

  if (isHiddenByRelatedInput(filter, values)) return null;
  if (filter.hiddenIf?.(row, values)) return null;

  const commonProps = {
    field,
    meta,
    helpers,
    filter,
    classes,
    row,
    rows,
    multipleSelection,
    isMappingUhCat,
    dynamicPath,
    onDisplaySubCreationForm,
    toggleMap,
    setToggleState,
  };

  return (
    <Box
      sx={[
        {
          flex: filter.flex,
          alignSelf: "start",
          // Use marginTop only for checkbox to avoid margin problem
          marginTop: filter.type === ConfigurationFormType.Check ? 1 : 0,
        },
        ...(Array.isArray(filter.wrapperSx)
          ? filter.wrapperSx
          : [filter.wrapperSx]),
      ]}
    >
      <Box
        sx={
          // A lookup spaces itself, and only when it renders something.
          contrastedStyles || filter.type === ConfigurationFormType.RemoteLookup
            ? {
                m: 0,
                "& .MuiMultiInputDateRangeField-root": { mt: 1 },
              }
            : isNestedForm
            ? { mx: 1 }
            : { m: 1 }
        }
      >
        <FieldRenderer {...commonProps} />
        {filter.helperRender && filter.helperRender()}
      </Box>
    </Box>
  );
};

export const FormBuilder = memo(
  ({
    dynamicPath,
    fieldsOptions,
    isMappingUhCat,
    keyFieldPath,
    multipleSelection,
    isNestedForm,
    onDisplaySubCreationForm,
    row,
    rows,
    contrastedStyles,
  }: {
    dynamicPath?: boolean;
    fieldsOptions: any;
    isMappingUhCat?: boolean;
    keyFieldPath?: string;
    multipleSelection?: GridRowSelectionModel;
    isNestedForm?: boolean;
    onDisplaySubCreationForm?: (
      formOptions: ConfigurationPageTable,
      parentFormValues: GenericObject,
      initialValues: GenericObject
    ) => void;
    row?: GridRowModel;
    rows?: GridRowModel[];
    contrastedStyles?: boolean;
  }) => {
    const { classes } = formInputsStyles();
    const { values } = useFormikContext<GenericObject>();

    /** TODO: move this logic in formik management values system */
    // ToggleMap is intended to manage the state of toggles in the form
    // Each toggle is identified by its key, and the value indicates whether it is open or closed
    const [toggleMap, setToggleMap] = useState<{ [key: string]: boolean }>({});
    // Stable identity and no-op on an unchanged value: each ToggleSection
    // reports its state from an effect keyed on this callback, so a new
    // function or a new map on every render would re-run those effects and
    // cascade into every field's visibility effect.
    const setToggleState = useCallback((key: string, value: boolean) => {
      setToggleMap((prevState) =>
        prevState[key] === value ? prevState : { ...prevState, [key]: value }
      );
    }, []);

    // Group each ToggleSection together with its consecutive child fields
    // (fields whose `toggleParent` matches the toggle's `key`). When open,
    // the children render inside a contrasted container — visually grouping
    // them with the toggle the same way FieldGroup wraps its rows.
    const renderItems: Array<
      | { kind: "field"; filter: any; index: number }
      | {
          kind: "toggleGroup";
          toggle: any;
          children: any[];
          index: number;
        }
    > = [];
    if (fieldsOptions) {
      let i = 0;
      while (i < fieldsOptions.length) {
        const filter = fieldsOptions[i];
        if (filter?.type === ConfigurationFormType.ToggleSection) {
          const children: any[] = [];
          let j = i + 1;
          while (
            j < fieldsOptions.length &&
            fieldsOptions[j]?.toggleParent === filter.key
          ) {
            children.push(fieldsOptions[j]);
            j++;
          }
          renderItems.push({
            kind: "toggleGroup",
            toggle: filter,
            children,
            index: i,
          });
          i = j;
        } else {
          renderItems.push({ kind: "field", filter, index: i });
          i++;
        }
      }
    }

    const renderInputBuilder = (filter: any, index: number) => (
      <InputBuilder
        key={`${filter.type}-${index}`}
        filter={filter}
        keyFieldPath={keyFieldPath}
        row={row}
        rows={rows}
        multipleSelection={multipleSelection}
        isMappingUhCat={isMappingUhCat}
        dynamicPath={dynamicPath}
        onDisplaySubCreationForm={onDisplaySubCreationForm}
        toggleMap={toggleMap}
        setToggleState={setToggleState}
        isNestedForm={isNestedForm}
        contrastedStyles={contrastedStyles}
      />
    );

    return (
      <Row
        className={`${isNestedForm ? classes.root : classes.rootMargin} ${
          contrastedStyles ? classes.contrastContainer : ""
        }`}
      >
        {renderItems.map((item) => {
          if (item.kind === "field") {
            return renderInputBuilder(item.filter, item.index);
          }
          if (isHiddenByRelatedInput(item.toggle, values)) return null;
          const isToggleOpen = toggleMap[item.toggle.key] ?? false;
          return (
            <Box
              key={`toggle-${item.toggle.key}-${item.index}`}
              sx={{ flex: "1 1 100%" }}
            >
              {renderInputBuilder(item.toggle, item.index)}
              {isToggleOpen && (
                <Box
                  sx={(theme) => ({
                    backgroundColor: theme.vars.palette.background.sunken,
                    borderRadius: "6px",
                    py: 1,
                    my: 1,
                  })}
                >
                  <Row
                    className={isNestedForm ? classes.root : classes.rootMargin}
                  >
                    {item.children.map((child, idx) =>
                      renderInputBuilder(child, item.index + 1 + idx)
                    )}
                  </Row>
                </Box>
              )}
            </Box>
          );
        })}
      </Row>
    );
  }
);
